/**
 * chartService — catalog helpers used by the in-app chart builder.
 *
 * Backed by data-service's `/catalog/cached/*` endpoints (read-only
 * access to the workspace's cached Snowflake metadata). The actual
 * chart `data` for a given dashboard item is loaded with
 * `executeDashboardSQL` from `dashboardService.js`.
 */

import { getCachedDatabaseCatalog, getCachedViewColumns } from './dataService';

/**
 * List schemas + views visible in the workspace warehouse.
 *
 * Adapts the EE-DATA-ASSISTANT catalog response shape
 * `{schemas: [{SCHEMA, VIEWS: "v1,v2,..."}, ...]}` to the chart-editor
 * shape `{schemas: [{name, tables: [string]}, ...]}`.
 */
export async function listSchemas() {
  const data = await getCachedDatabaseCatalog();
  const schemas = (data?.schemas || []).map((s) => {
    const name = s.SCHEMA || s.name || '';
    const viewsStr = s.VIEWS || '';
    const tables = typeof viewsStr === 'string'
      ? viewsStr.split(',').map((v) => v.trim()).filter(Boolean).sort()
      : (Array.isArray(viewsStr) ? viewsStr : []);
    return { name, tables };
  });
  return { schemas };
}

/**
 * Describe one view — column names, types.
 * Returns: { schema, table, columns: [{name, type}] }
 */
export async function describeTable(schema, table) {
  const data = await getCachedViewColumns(schema, table);
  const view = data?.view || {};
  const columns = (view.COLUMNS || []).map((c) => ({
    name: c.COLUMN_NAME || c.name,
    type: c.TYPE || c.type || '',
    nullable: c.NULLABLE,
  }));
  return { schema, table, columns };
}

/**
 * Sample N rows from a view via the data-service execute-sql endpoint.
 */
export async function sampleTable(schema, table, limit = 200) {
  const sql = `SELECT * FROM "${schema}"."${table}" LIMIT ${Math.max(1, Math.min(500, limit))}`;
  const { postRequest } = await import('./api');
  return postRequest('data', '/execute-sql', { sql, limit, skip_cache: false });
}

/**
 * Build an explicit-column SELECT for a view. Used to seed the SQL
 * editor with a clear list of fields the user can prune / extend
 * instead of an opaque ``SELECT *``.
 */
export function buildExplicitSelect(schema, table, columns, limit = 200) {
  const cols = (columns || []).map((c) => `"${c}"`).join(',\n  ');
  if (!cols) return `SELECT * FROM "${schema}"."${table}" LIMIT ${limit}`;
  return `SELECT\n  ${cols}\nFROM "${schema}"."${table}"\nLIMIT ${limit}`;
}

// chartAssist() removed: AI-assisted chart edits are now handled by the main
// data agent (data-service /query) with a `chart_editor_context` payload.
// See DataChat.handleSendMessage and DataAgent's CHART EDITOR MODE prompt.
